import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Buildings, Plus, SignIn, PencilSimple, ProhibitInset, CheckCircle, Users, ListChecks, Briefcase, MapPin, SignOut, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatDate } from "../lib/format";
import Logo from "../components/Logo";

const INDUSTRIES = [
  { v: "mineria", label: "Minería" },
  { v: "municipio", label: "Municipio" },
  { v: "energia", label: "Energía" },
  { v: "forestal", label: "Forestal" },
  { v: "ong", label: "ONG / Fundación" },
  { v: "otro", label: "Otro" },
];

const emptyClient = () => ({
  name: "", rut: "", industry: "mineria", description: "", logo_url: "",
  admin_full_name: "", admin_email: "", admin_password: "",
});

export default function AdminClients() {
  const { user, logout, impersonateClient } = useAuth();
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyClient());
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/clients");
      setClients(data);
    } catch (e) {
      toast.error("No se pudo cargar la lista de clientes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyClient());
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      rut: c.rut || "",
      industry: c.industry || "mineria",
      description: c.description || "",
      logo_url: c.logo_url || "",
      admin_full_name: "",
      admin_email: "",
      admin_password: "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/clients/${editing.id}`, {
          name: form.name,
          rut: form.rut || null,
          industry: form.industry || null,
          description: form.description || null,
          logo_url: form.logo_url || null,
        });
        toast.success("Cliente actualizado");
      } else {
        if (!form.admin_email || !form.admin_password || !form.admin_full_name) {
          toast.error("Completa los datos del administrador inicial");
          setSaving(false);
          return;
        }
        await api.post("/admin/clients", form);
        toast.success(`Cliente "${form.name}" creado`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el cliente");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      if (c.is_active) {
        await api.delete(`/admin/clients/${c.id}`);
        toast.success("Cliente desactivado");
      } else {
        await api.put(`/admin/clients/${c.id}`, { is_active: true });
        toast.success("Cliente reactivado");
      }
      load();
    } catch {
      toast.error("No se pudo cambiar el estado");
    }
  };

  const enterAsClient = async (c) => {
    try {
      await impersonateClient(c.id);
      toast.success(`Ingresando como ${c.name}`);
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la sesión");
    }
  };

  const manageUsers = async (c) => {
    try {
      await impersonateClient(c.id);
      toast.success(`Gestionando usuarios de ${c.name}`);
      nav("/usuarios");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la sesión");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-clients-page">
      {/* Top bar */}
      <header className="bg-slate-900 text-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={36} className="text-white" />
            <div>
              <div className="font-display text-xl font-bold text-white leading-none">SIGSAOP</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-1">
                Panel Super Admin
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right text-xs leading-tight">
              <div className="text-slate-200 font-medium">{user?.full_name}</div>
              <div className="text-slate-400">{user?.email}</div>
            </div>
            <button
              onClick={() => { logout(); nav("/login"); }}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-300 hover:bg-slate-800"
            >
              <SignOut size={16} />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="font-display text-4xl font-bold text-slate-900 tracking-tight">
              Clientes
            </h1>
            <p className="text-slate-600 mt-2 max-w-2xl">
              Empresas y organizaciones que utilizan la plataforma. Crea nuevos clientes,
              edita su información, desactívalos o entra como cualquiera de ellos para
              ver su data en modo administrador.
            </p>
          </div>
          <Button data-testid="new-client-btn" onClick={openCreate} className="gap-2">
            <Plus size={16} /> Nuevo cliente
          </Button>
        </div>

        {/* Stats global */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-10">
          <Stat label="Clientes" value={clients.length} icon={Buildings} />
          <Stat label="Clientes activos" value={clients.filter(c => c.is_active).length} icon={CheckCircle} />
          <Stat label="Usuarios totales" value={clients.reduce((s, c) => s + c.users_count, 0)} icon={Users} />
          <Stat label="Compromisos totales" value={clients.reduce((s, c) => s + c.commitments_count, 0)} icon={ListChecks} />
        </div>

        {loading ? (
          <div className="text-slate-400 py-12 text-center">Cargando…</div>
        ) : clients.length === 0 ? (
          <div className="text-slate-400 py-12 text-center">Aún no hay clientes. Crea el primero.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {clients.map((c) => (
              <div
                key={c.id}
                data-testid={`client-card-${c.id}`}
                className={`bg-white border rounded-lg p-5 shadow-sm flex flex-col gap-4 ${!c.is_active ? "opacity-60" : ""}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-12 h-12 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                      {c.logo_url ? (
                        <img src={c.logo_url} alt={c.name} className="w-full h-full object-cover rounded-md" />
                      ) : (
                        <Buildings size={22} className="text-slate-500" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-lg font-bold text-slate-900 truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                        {c.rut && <span>RUT {c.rut}</span>}
                        {c.industry && <span className="px-2 py-0.5 bg-slate-100 rounded">{c.industry}</span>}
                        {!c.is_active && <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded">Inactivo</span>}
                      </div>
                      {c.description && (
                        <div className="text-xs text-slate-500 mt-2 line-clamp-2">{c.description}</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 text-center text-xs">
                  <Mini icon={Users} v={c.users_count} l="Usuarios" />
                  <Mini icon={MapPin} v={c.communities_count} l="Comunidades" />
                  <Mini icon={Briefcase} v={c.projects_count} l="Proyectos" />
                  <Mini icon={ListChecks} v={c.commitments_count} l="Compromisos" />
                </div>

                <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">
                    Creado {formatDate(c.created_at)}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`edit-client-${c.id}`}
                      onClick={() => openEdit(c)}
                      className="p-2 rounded-md text-slate-600 hover:bg-slate-100"
                      title="Editar"
                    >
                      <PencilSimple size={16} />
                    </button>
                    <button
                      data-testid={`toggle-client-${c.id}`}
                      onClick={() => toggleActive(c)}
                      className={`p-2 rounded-md ${c.is_active ? "text-red-600 hover:bg-red-50" : "text-brand-green-600 hover:bg-brand-green-50"}`}
                      title={c.is_active ? "Desactivar" : "Reactivar"}
                    >
                      {c.is_active ? <ProhibitInset size={16} /> : <CheckCircle size={16} />}
                    </button>
                    {c.is_active && (
                      <>
                        <button
                          data-testid={`manage-users-${c.id}`}
                          onClick={() => manageUsers(c)}
                          className="p-2 rounded-md text-slate-600 hover:bg-slate-100"
                          title="Gestionar usuarios"
                        >
                          <ShieldCheck size={16} />
                        </button>
                        <Button
                          data-testid={`enter-client-${c.id}`}
                          onClick={() => enterAsClient(c)}
                          size="sm"
                          className="gap-2"
                        >
                          <SignIn size={14} /> Entrar como
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar cliente" : "Nuevo cliente"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Actualiza los datos de la empresa. El usuario administrador no se modifica desde aquí."
                : "Crea una empresa nueva en SIGSAOP. Se generará automáticamente un usuario administrador inicial."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nombre de la empresa *</Label>
                <Input
                  data-testid="client-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>RUT</Label>
                <Input
                  data-testid="client-rut-input"
                  value={form.rut}
                  onChange={(e) => setForm({ ...form, rut: e.target.value })}
                  placeholder="76.123.456-7"
                />
              </div>
              <div>
                <Label>Industria</Label>
                <select
                  data-testid="client-industry-input"
                  className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                  value={form.industry}
                  onChange={(e) => setForm({ ...form, industry: e.target.value })}
                >
                  {INDUSTRIES.map((i) => <option key={i.v} value={i.v}>{i.label}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <Label>URL del logo (opcional)</Label>
                <Input
                  data-testid="client-logo-input"
                  value={form.logo_url}
                  onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                  placeholder="https://…"
                />
              </div>
              <div className="col-span-2">
                <Label>Descripción</Label>
                <Textarea
                  rows={2}
                  data-testid="client-description-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
            </div>

            {!editing && (
              <div className="space-y-3 border-t border-slate-200 pt-4">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Usuario administrador inicial
                </div>
                <div>
                  <Label>Nombre completo *</Label>
                  <Input
                    data-testid="admin-full-name-input"
                    value={form.admin_full_name}
                    onChange={(e) => setForm({ ...form, admin_full_name: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      data-testid="admin-email-input"
                      type="email"
                      value={form.admin_email}
                      onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Contraseña *</Label>
                    <Input
                      data-testid="admin-password-input"
                      type="text"
                      value={form.admin_password}
                      onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                      placeholder="Mín. 6 caracteres"
                      required
                    />
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 leading-snug">
                  Este usuario podrá ingresar a SIGSAOP con su email y contraseña, y administrar la cuenta de la empresa.
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="save-client-btn" type="submit" disabled={saving}>
                {saving ? "Guardando…" : (editing ? "Guardar cambios" : "Crear cliente")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
        <Icon size={18} className="text-slate-400" />
      </div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function Mini({ icon: Icon, v, l }) {
  return (
    <div className="flex flex-col items-center gap-1 bg-slate-50 rounded p-2">
      <Icon size={14} className="text-slate-500" />
      <div className="font-display font-bold text-base text-slate-900 leading-none">{v}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{l}</div>
    </div>
  );
}
